import React, { useState } from "react";
import {
  TextField,
  SxProps,
  Theme,
  InputAdornment,
  IconButton,
} from "@mui/material";

import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";

interface PasswordInputProps {
  sx?: SxProps<Theme>;
  value?: string;
  onChange?: (val: string, field?: string) => void;
  label: string;
  field?: string;
  error?: string;
  disabled?: boolean;
  required?: boolean;
  size?: "small" | "medium";
  InputLabelProps?: any;
  autoComplete?: string;
}

const PasswordInput = ({
  sx,
  label,
  value,
  onChange,
  field,
  error,
  disabled = false,
  required = false,
  size = "small",
  InputLabelProps,
  autoComplete = "current-password",
}: PasswordInputProps) => {
  const [showPassword, setShowPassword] = useState(false);
  const [isAutofilled, setIsAutofilled] = useState(false);

  const handleAnimationStart = (e: React.AnimationEvent<HTMLInputElement>) => {
    if (e.animationName === "mui-auto-fill") {
      setIsAutofilled(true);
    } else if (e.animationName === "mui-auto-fill-cancel") {
      setIsAutofilled(false);
    }
  };

  const hasValue = value !== "" && value !== undefined && value !== null;

  return (
    <TextField
      fullWidth
      size={size}
      label={label}
      variant="outlined"
      type={showPassword ? "text" : "password"}
      value={value ?? ""}
      disabled={disabled}
      required={required}
      error={Boolean(error)}
      helperText={error || undefined}
      autoComplete={autoComplete}
      onChange={(event) => {
        onChange?.(event.target.value, field);
      }}
      InputLabelProps={{
        shrink: hasValue || isAutofilled ? true : undefined,
        ...InputLabelProps,
      }}
      InputProps={{
        endAdornment: (
          <InputAdornment position="end">
            <IconButton
              onClick={() => setShowPassword((prev) => !prev)}
              edge="end"
              aria-label={showPassword ? "Hide password" : "Show password"}
            >
              {showPassword ? <VisibilityOff /> : <Visibility />}
            </IconButton>
          </InputAdornment>
        ),
      }}
      inputProps={{
        onAnimationStart: handleAnimationStart,
      }}
      sx={{
        "& .MuiOutlinedInput-root": {
          pr: 1,
        },
        "& input:-webkit-autofill": {
          WebkitBoxShadow: "0 0 0 1000px #fff inset !important",
          WebkitTextFillColor: "#0F172A !important",
          borderRadius: "10px",
        },
        "& .MuiFormHelperText-root": {
          marginLeft: "2px",
        },
        ...sx,
      }}
    />
  );
};

export default PasswordInput;
